import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { invoices as invoicesApi, businesses } from '../api';
import { formatCurrency, formatDate, todayISO, getCurrencySymbol } from '../utils/format';
import { getCountryConfig } from '../utils/countries';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';

const PAYMENT_MODES = ['UPI', 'Bank Transfer', 'Cash', 'Cheque', 'Card', 'Other'];

const WA_ICON = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.525 5.845L.057 23.25c-.078.306.042.63.308.805a.693.693 0 00.71.015l5.637-2.947A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.806 9.806 0 01-5.015-1.376l-.36-.213-3.724 1.949 1.026-3.655-.236-.375A9.785 9.785 0 012.182 12c0-5.422 4.396-9.818 9.818-9.818 5.421 0 9.818 4.396 9.818 9.818 0 5.422-4.397 9.818-9.818 9.818z" />
  </svg>
);

const MAIL_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const PDF_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const DisabledAction = ({ icon, label, tooltip, colorClass }) => (
  <div className="relative group">
    <button disabled className={`flex items-center justify-center gap-2 ${colorClass} text-white text-sm font-medium py-2 px-4 rounded-lg opacity-40 cursor-not-allowed w-full`}>
      {icon}{label}
    </button>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-20">
      {tooltip}
    </div>
  </div>
);

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentModal, setPaymentModal] = useState(false);
  const [payForm, setPayForm] = useState({ payment_date: todayISO(), payment_mode: 'UPI', amount: '' });
  const [saving, setSaving] = useState(false);
  const [waConfirmOpen, setWaConfirmOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingReminderEmail, setSendingReminderEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [reminderLogs, setReminderLogs] = useState([]);

  const load = () => {
    Promise.all([
      invoicesApi.get(id),
      businesses.get(),
    ]).then(([invRes, bizRes]) => {
      setInvoice(invRes.data);
      setBusiness(bizRes.data);
      setLoading(false);
    });
    invoicesApi.getReminderLogs(id).then(r => setReminderLogs(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (invoice) {
      setPayForm(f => ({ ...f, amount: invoice.balance_due > 0 ? invoice.balance_due.toFixed(2) : '' }));
    }
  }, [invoice]);

  const recordPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) return;
    setSaving(true);
    try {
      await invoicesApi.addPayment(id, {
        ...payForm,
        amount: parseFloat(payForm.amount),
      });
      setPaymentModal(false);
      load();
    } finally { setSaving(false); }
  };

  const deletePayment = async (paymentId) => {
    await invoicesApi.deletePayment(id, paymentId);
    load();
  };

  const updateStatus = async (status) => {
    await invoicesApi.updateStatus(id, status);
    load();
  };

  const handleWhatsAppClick = () => {
    if (invoice.status === 'draft') setWaConfirmOpen(true);
  };

  const confirmMarkSent = async () => {
    setSaving(true);
    try {
      await updateStatus('sent');
      setWaConfirmOpen(false);
    } finally { setSaving(false); }
  };

  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const res = await invoicesApi.downloadPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const cancelInvoiceOnly = async () => {
    setCancelling(true);
    try {
      await invoicesApi.cancel(id);
      setCancelModalOpen(false);
      load();
    } finally { setCancelling(false); }
  };

  const cancelAndNotify = async () => {
    setCancelling(true);
    try {
      await invoicesApi.cancel(id, invoice.client_email ? { notify_email: true } : undefined);
      if (whatsappNum) {
        window.open(`https://wa.me/${whatsappNum}?text=${buildCancelMessage()}`, '_blank');
      }
      setCancelModalOpen(false);
      load();
    } finally { setCancelling(false); }
  };

  const sendInvoiceViaEmail = async () => {
    setSendingEmail(true);
    setEmailStatus(null);
    try {
      const res = await invoicesApi.sendEmail(id);
      setEmailStatus({ type: 'success', text: `Invoice sent to ${res.data.email}` });
      load();
    } catch (err) {
      if (err.response?.data?.code === 'PRO_FEATURE') { navigate('/pricing'); return; }
      setEmailStatus({ type: 'error', text: err.response?.data?.error || 'Failed to send invoice email' });
    } finally { setSendingEmail(false); }
  };

  const sendReminderViaEmail = async () => {
    setSendingReminderEmail(true);
    setEmailStatus(null);
    try {
      const res = await invoicesApi.sendReminderEmail(id);
      setEmailStatus({ type: 'success', text: `Reminder sent to ${res.data.email}` });
    } catch (err) {
      if (err.response?.data?.code === 'PRO_FEATURE') { navigate('/pricing'); return; }
      setEmailStatus({ type: 'error', text: err.response?.data?.error || 'Failed to send reminder email' });
    } finally { setSendingReminderEmail(false); }
  };

  const invoiceLink = `${window.location.origin}/view/${id}`;

  const daysOverdue = () => {
    if (!invoice) return 0;
    const due = new Date(invoice.due_date);
    const today = new Date(todayISO());
    return Math.max(0, Math.floor((today - due) / (1000 * 60 * 60 * 24)));
  };

  const buildReminderMessage = () => {
    if (!invoice || !business) return '';
    const overdueDays = daysOverdue();
    const isOverdueMsg = overdueDays > 0 && invoice.status !== 'paid' && invoice.status !== 'cancelled';
    const paymentDetails = business.reminder_include_payment_details && business.reminder_payment_details_text
      ? `${business.reminder_payment_details_text}\n\n`
      : '';

    let msg;
    if (isOverdueMsg) {
      msg = `Dear ${invoice.client_name},\n\n` +
        `This is an urgent reminder for Invoice *${invoice.invoice_number}* dated ${formatDate(invoice.invoice_date)}.\n\n` +
        `⚠️ This invoice is *overdue by ${overdueDays} day${overdueDays !== 1 ? 's' : ''}*.\n\n` +
        `Amount Due: *${formatCurrency(invoice.balance_due)}*\n` +
        `Original Due Date: ${formatDate(invoice.due_date)}\n\n` +
        paymentDetails +
        `Please make the payment immediately or contact us to discuss.\n\n` +
        `View Invoice: ${invoiceLink}\n\n` +
        `Regards,\n${business.name}`;
    } else {
      msg = `Dear ${invoice.client_name},\n\n` +
        `This is a reminder for Invoice *${invoice.invoice_number}*.\n\n` +
        `Invoice: *${invoice.invoice_number}*\n` +
        `Date: ${formatDate(invoice.invoice_date)}\n` +
        `Due Date: ${formatDate(invoice.due_date)}\n` +
        `Amount Due: *${formatCurrency(invoice.balance_due)}*\n\n` +
        paymentDetails +
        `Please make the payment at the earliest.\n\n` +
        `Regards,\n${business.name}\n\n` +
        `View Invoice: ${invoiceLink}`;
    }

    return encodeURIComponent(msg);
  };

  const buildSendInvoiceMessage = () => {
    if (!invoice || !business) return '';
    const msg =
      `Dear ${invoice.client_name},\n\n` +
      `Please find your invoice from *${business.name}*.\n\n` +
      `Invoice: *${invoice.invoice_number}*\n` +
      `Date: ${formatDate(invoice.invoice_date)}\n` +
      `Due Date: ${formatDate(invoice.due_date)}\n` +
      `Amount: *${formatCurrency(invoice.total_amount)}*\n\n` +
      (business.bank_details ? `Bank: ${business.bank_details}\n` : '') +
      `\nView your invoice here: ${invoiceLink}\n\n` +
      `Regards,\n${business.name}`;
    return encodeURIComponent(msg);
  };

  const buildCancelMessage = () => {
    if (!invoice || !business) return '';
    const msg =
      `Dear ${invoice.client_name},\n\n` +
      `Please note that Invoice *${invoice.invoice_number}* dated ${formatDate(invoice.invoice_date)} for *${formatCurrency(invoice.total_amount)}* has been cancelled and is no longer valid.\n\n` +
      `No payment is required for this invoice.\n\n` +
      `For any queries, please contact us.\n\n` +
      `Regards,\n${business.name}`;
    return encodeURIComponent(msg);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!invoice) return <div className="text-center py-20 text-gray-500">Invoice not found</div>;

  const reminderMsg = buildReminderMessage();
  const sendInvoiceMsg = buildSendInvoiceMessage();
  const whatsappNum = invoice.client_whatsapp?.replace(/\D/g, '');
  const isDraft = invoice.status === 'draft';
  const isSentGroup = invoice.status === 'sent' || invoice.status === 'overdue';
  const isLocked = invoice.status === 'paid' || invoice.status === 'cancelled';
  const canCancel = ['draft', 'sent', 'overdue'].includes(invoice.status) && (invoice.amount_paid || 0) === 0;
  const currSymbol = getCurrencySymbol();
  const taxLabel = getCountryConfig(business?.country).invoiceTaxLabel;
  const totalsTaxLabel = getCountryConfig(business?.country).totalsTaxLabel;

  return (
    <div>
      <PageHeader
        title={invoice.invoice_number}
        subtitle={`Created ${formatDate(invoice.created_at)}`}
        actions={
          !isLocked && (
            <div className="flex items-center gap-2 print-hide">
              {isDraft ? (
                <Link to={`/invoices/${id}/edit`} className="btn-secondary">Edit</Link>
              ) : (
                <div className="relative group inline-block">
                  <button
                    disabled
                    className="btn-secondary opacity-40 cursor-not-allowed"
                  >
                    Edit
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                    Only draft invoices can be edited
                  </div>
                </div>
              )}
              <div className="relative group">
                <button className="btn-secondary">More ▾</button>
                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-100 rounded-lg shadow-lg hidden group-hover:block z-10 text-sm">
                  {['draft', 'sent', 'paid', 'overdue'].filter(s => s !== invoice.status).map(s => (
                    <button key={s} onClick={() => updateStatus(s)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 capitalize text-gray-700 first:rounded-t-lg last:rounded-b-lg">
                      Mark as {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Preview */}
        <div className="lg:col-span-2">
          <div className="card" id="invoice-print-area">
            {/* Header */}
            <div className="flex flex-row gap-4 mb-6">
              {/* Left: Logo stacked above business name + address */}
              <div className="flex-1 min-w-0">
                {business?.logo_url && (
                  <img
                    src={business.logo_url}
                    alt="Logo"
                    className="w-16 h-16 object-contain mb-2"
                  />
                )}
                <p className="text-base md:text-xl font-bold text-gray-900 break-words">
                  {business?.name}
                </p>
                <p className="text-xs text-gray-600 mt-0.5 break-words whitespace-pre-line">
                  {business?.address}
                </p>
                {business?.gst_number && (
                  <p className="text-xs text-gray-500">{taxLabel}: {business.gst_number}</p>
                )}
                {business?.email && (
                  <p className="text-xs text-gray-500 break-all">{business.email}</p>
                )}
                {business?.phone && (
                  <p className="text-xs text-gray-500">{business.phone}</p>
                )}
              </div>
              {/* Right: INVOICE label + number + status — right aligned */}
              <div className="flex-shrink-0 text-right">
                <p className="text-xs font-bold tracking-widest text-gray-300 uppercase">
                  Invoice
                </p>
                <p className="text-sm md:text-base font-bold text-gray-800">
                  {invoice.invoice_number}
                </p>
                <div className="mt-1 flex justify-end">
                  <StatusBadge status={invoice.status} />
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Bill To</p>
                <p className="font-semibold text-gray-900">{invoice.client_name}</p>
                {invoice.client_company && <p className="text-sm text-gray-600">{invoice.client_company}</p>}
                {invoice.client_address && <p className="text-sm text-gray-500 whitespace-pre-line">{invoice.client_address}</p>}
                {invoice.client_email && <p className="text-sm text-gray-500">{invoice.client_email}</p>}
                {invoice.client_gst_number && <p className="text-xs text-gray-400">{taxLabel}: {invoice.client_gst_number}</p>}
              </div>
              <div className="text-right">
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-gray-400">Invoice Date: </span>
                    <span className="text-gray-700 font-medium">{formatDate(invoice.invoice_date)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Due Date: </span>
                    <span className="text-gray-700 font-medium">{formatDate(invoice.due_date)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm min-w-[500px]">
                <thead>
                  <tr className="bg-primary-700 text-white">
                    <th className="text-left px-4 py-2 rounded-tl-lg font-medium">#</th>
                    <th className="text-left px-4 py-2 font-medium">Description</th>
                    <th className="text-right px-4 py-2 font-medium">Qty</th>
                    <th className="text-right px-4 py-2 font-medium">Rate</th>
                    <th className="text-right px-4 py-2 rounded-tr-lg font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((item, idx) => (
                    <tr key={item.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-2.5 text-gray-400">{idx + 1}</td>
                      <td className="px-4 py-2.5 text-gray-700">{item.description}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{item.quantity}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{formatCurrency(item.rate)}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-64 space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{totalsTaxLabel} ({invoice.gst_percentage}%)</span>
                  <span>{formatCurrency(invoice.gst_amount)}</span>
                </div>
                <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-200 pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(invoice.total_amount)}</span>
                </div>
                {invoice.amount_paid > 0 && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>Paid</span>
                      <span>- {formatCurrency(invoice.amount_paid)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-primary-700 border-t border-gray-200 pt-2">
                      <span>Balance Due</span>
                      <span>{formatCurrency(invoice.balance_due)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Notes</p>
                <p className="text-sm text-gray-600 whitespace-pre-line">{invoice.notes}</p>
              </div>
            )}

            {/* Payment Details */}
            {business?.bank_details && (
              <div className="border-t border-gray-100 pt-4 mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Payment Details</p>
                <div className="text-sm">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium mb-0.5">Bank Details</p>
                    <p className="text-blue-800 whitespace-pre-line text-xs">{business.bank_details}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="space-y-4 print-hide lg:sticky lg:top-6 lg:self-start">
          {/* Balance */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">Payment Status</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-semibold">{formatCurrency(invoice.total_amount)}</span></div>
              <div className="flex justify-between text-green-600"><span>Received</span><span className="font-semibold">{formatCurrency(invoice.amount_paid)}</span></div>
              <div className="flex justify-between text-primary-700 border-t pt-2"><span className="font-bold">Balance</span><span className="font-bold text-lg">{formatCurrency(invoice.balance_due)}</span></div>
            </div>
          </div>

          {/* Actions */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Actions</h3>
            {emailStatus && (
              <div className={`mb-3 px-3 py-2 rounded-lg text-sm flex items-start justify-between gap-2 ${emailStatus.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                <span>{emailStatus.text}</span>
                <button onClick={() => setEmailStatus(null)} className="text-xs opacity-60 hover:opacity-100">✕</button>
              </div>
            )}
            <div className="space-y-2">
              {isDraft && (
                <>
                  {whatsappNum ? (
                    <a
                      href={`https://wa.me/${whatsappNum}?text=${sendInvoiceMsg}`}
                      target="_blank" rel="noreferrer"
                      onClick={handleWhatsAppClick}
                      className="flex items-center justify-center gap-2 bg-green-600 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-green-700 transition-colors w-full"
                    >
                      {WA_ICON}
                      Send via WhatsApp
                    </a>
                  ) : (
                    <div title="No WhatsApp number on client profile">
                      <button disabled className="flex items-center justify-center gap-2 bg-green-600 text-white text-sm font-medium py-2 px-4 rounded-lg opacity-40 cursor-not-allowed w-full">
                        {WA_ICON}
                        Send via WhatsApp
                      </button>
                      <p className="text-xs text-gray-400 mt-1">Add WhatsApp number to client profile</p>
                    </div>
                  )}
                  {invoice.client_email ? (
                    <button
                      onClick={sendInvoiceViaEmail}
                      disabled={sendingEmail}
                      className="flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors w-full disabled:opacity-50"
                    >
                      {MAIL_ICON}
                      {sendingEmail ? 'Sending...' : 'Send via Email'}
                    </button>
                  ) : (
                    <div title="No email address on client profile">
                      <button disabled className="flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-lg opacity-40 cursor-not-allowed w-full">
                        {MAIL_ICON}
                        Send via Email
                      </button>
                      <p className="text-xs text-gray-400 mt-1">Add email address to client profile</p>
                    </div>
                  )}
                  <DisabledAction icon={null} label="Record Payment" tooltip="Mark as Sent first" colorClass="bg-primary-700" />
                  <button
                    onClick={downloadPDF}
                    disabled={downloadingPdf}
                    className="flex items-center justify-center gap-2 bg-gray-700 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors w-full disabled:opacity-50"
                  >
                    {PDF_ICON}
                    {downloadingPdf ? 'Preparing...' : 'Download PDF'}
                  </button>
                </>
              )}

              {isSentGroup && (
                <>
                  {whatsappNum ? (
                    <a
                      href={`https://wa.me/${whatsappNum}?text=${reminderMsg}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center justify-center gap-2 bg-green-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg hover:bg-green-700 transition-colors w-full"
                    >
                      {WA_ICON}
                      Send Reminder
                    </a>
                  ) : (
                    <div title="No WhatsApp number on client profile">
                      <button disabled className="flex items-center justify-center gap-2 bg-green-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg opacity-40 cursor-not-allowed w-full">
                        {WA_ICON}
                        Send Reminder
                      </button>
                      <p className="text-xs text-gray-400 mt-1">Add WhatsApp number to client profile</p>
                    </div>
                  )}

                  {invoice.client_email ? (
                    <button
                      onClick={sendReminderViaEmail}
                      disabled={sendingReminderEmail}
                      className="flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors w-full disabled:opacity-50"
                    >
                      {MAIL_ICON}
                      {sendingReminderEmail ? 'Sending...' : 'Send Reminder Email'}
                    </button>
                  ) : (
                    <div title="No email address on client profile">
                      <button disabled className="flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg opacity-40 cursor-not-allowed w-full">
                        {MAIL_ICON}
                        Send Reminder Email
                      </button>
                      <p className="text-xs text-gray-400 mt-1">Add email address to client profile</p>
                    </div>
                  )}

                  {invoice.balance_due > 0 && (
                    <button
                      onClick={() => setPaymentModal(true)}
                      className="flex items-center justify-center gap-2 bg-green-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg hover:bg-green-700 transition-colors w-full"
                    >
                      Record Payment
                    </button>
                  )}

                  <div className="border-t border-gray-100 my-2" />

                  {whatsappNum ? (
                    <a
                      href={`https://wa.me/${whatsappNum}?text=${sendInvoiceMsg}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-[#1ebc59] transition-colors w-full"
                    >
                      {WA_ICON}
                      Resend Invoice via WhatsApp
                    </a>
                  ) : (
                    <button disabled className="flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-medium py-2 px-4 rounded-lg opacity-40 cursor-not-allowed w-full">
                      {WA_ICON}
                      Resend Invoice via WhatsApp
                    </button>
                  )}

                  {invoice.client_email ? (
                    <button
                      onClick={sendInvoiceViaEmail}
                      disabled={sendingEmail}
                      className="flex items-center justify-center gap-2 bg-primary-500 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-primary-600 transition-colors w-full disabled:opacity-50"
                    >
                      {MAIL_ICON}
                      {sendingEmail ? 'Sending...' : 'Resend Invoice via Email'}
                    </button>
                  ) : (
                    <button disabled className="flex items-center justify-center gap-2 bg-primary-500 text-white text-sm font-medium py-2 px-4 rounded-lg opacity-40 cursor-not-allowed w-full">
                      {MAIL_ICON}
                      Resend Invoice via Email
                    </button>
                  )}

                  <button
                    onClick={downloadPDF}
                    disabled={downloadingPdf}
                    className="flex items-center justify-center gap-2 bg-gray-700 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors w-full disabled:opacity-50"
                  >
                    {PDF_ICON}
                    {downloadingPdf ? 'Preparing...' : 'Download PDF'}
                  </button>
                </>
              )}

              {isLocked && (
                <button
                  onClick={downloadPDF}
                  disabled={downloadingPdf}
                  className="flex items-center justify-center gap-2 bg-gray-700 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors w-full disabled:opacity-50"
                >
                  {PDF_ICON}
                  {downloadingPdf ? 'Preparing...' : 'Download PDF'}
                </button>
              )}
            </div>

            {canCancel && (
              <>
                <div className="border-t border-gray-100 my-3" />
                <button onClick={() => setCancelModalOpen(true)} className="btn-danger w-full">
                  Cancel Invoice
                </button>
              </>
            )}
          </div>

          {/* Payment History */}
          {invoice.payments.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Payment History</h3>
              <div className="space-y-2">
                {invoice.payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50">
                    <div>
                      <p className="font-medium text-gray-800">{formatCurrency(p.amount)}</p>
                      <p className="text-xs text-gray-400">{p.payment_mode} · {formatDate(p.payment_date)}</p>
                    </div>
                    <button onClick={() => deletePayment(p.id)} className="text-gray-300 hover:text-red-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Auto Reminders Sent */}
          {reminderLogs.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Auto Reminders Sent
              </h3>
              <div className="space-y-2">
                {reminderLogs.map(log => (
                  <div key={log.id} className="flex items-start gap-2 text-sm py-2 border-b border-gray-50 last:border-0">
                    <span className="text-lg leading-none mt-0.5">📧</span>
                    <div>
                      <p className="text-gray-700 font-medium">
                        {log.reminder_type === 'pre_due' ? 'Pre-due reminder' : 'Overdue reminder'}
                      </p>
                      <p className="text-xs text-gray-400">
                        Sent to {log.client_email} · {new Date(log.sent_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Record Payment Modal */}
      <Modal isOpen={paymentModal} onClose={() => setPaymentModal(false)} title="Record Payment" size="sm">
        <div className="space-y-4">
          <div>
            <label className="label">Amount ({currSymbol})</label>
            <input type="number" className="input" min="0" step="0.01"
              value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} />
          </div>
          <div>
            <label className="label">Payment Date</label>
            <input type="date" className="input" value={payForm.payment_date}
              onChange={e => setPayForm({ ...payForm, payment_date: e.target.value })} />
          </div>
          <div>
            <label className="label">Payment Mode</label>
            <select className="input" value={payForm.payment_mode}
              onChange={e => setPayForm({ ...payForm, payment_mode: e.target.value })}>
              {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6">
          <button className="btn-secondary w-full sm:w-auto" onClick={() => setPaymentModal(false)}>Cancel</button>
          <button className="btn-primary w-full sm:w-auto" onClick={recordPayment} disabled={saving}>
            {saving ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </Modal>

      {/* WhatsApp Sent Confirmation Modal */}
      <Modal isOpen={waConfirmOpen} onClose={() => setWaConfirmOpen(false)} title="Did you send the invoice?" size="sm">
        <p className="text-sm text-gray-600">
          We opened WhatsApp in a new tab with the invoice pre-filled. Let us know once you've sent it so we can update the invoice status.
        </p>
        <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6">
          <button className="btn-secondary w-full sm:w-auto" onClick={() => setWaConfirmOpen(false)}>Not Yet</button>
          <button className="btn-primary w-full sm:w-auto" onClick={confirmMarkSent} disabled={saving}>
            {saving ? 'Updating...' : 'Yes, Mark as Sent'}
          </button>
        </div>
      </Modal>

      {/* Cancel Invoice Modal */}
      <Modal isOpen={cancelModalOpen} onClose={() => setCancelModalOpen(false)} title="Cancel Invoice" size="sm">
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to cancel invoice <strong>{invoice.invoice_number}</strong>? This cannot be undone and the invoice will become read-only.
        </p>
        <div className="space-y-2">
          <button className="btn-secondary w-full" onClick={cancelInvoiceOnly} disabled={cancelling}>
            {cancelling ? 'Cancelling...' : 'Cancel Invoice Only'}
          </button>
          <button className="btn-danger w-full" onClick={cancelAndNotify} disabled={cancelling || !whatsappNum}>
            {cancelling ? 'Cancelling...' : 'Cancel and Notify Client via WhatsApp'}
          </button>
          {!whatsappNum && (
            <p className="text-xs text-gray-400 text-center">Add a WhatsApp number to the client profile to notify them</p>
          )}
        </div>
      </Modal>
    </div>
  );
}
